import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, FormControl, InputLabel, MenuItem, Select,
  Typography, IconButton, Alert, CircularProgress, TextField, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Menu
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as ViewIcon,
  PlayArrow as StartIcon, CheckCircle as CompleteIcon, Cancel as CancelIcon,
  Pause as HoldIcon, Send as SubmitIcon, ThumbUp as ApproveIcon,
  MoreVert as MoreIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import millingApi, { MillingOrder, OrderStatus, MillingRecipe } from '../../services/millingApi';

const statusColors: Record<OrderStatus, 'default' | 'warning' | 'info' | 'primary' | 'success' | 'error'> = {
  draft: 'default',
  pending_approval: 'warning',
  approved: 'info',
  in_progress: 'primary',
  completed: 'success',
  cancelled: 'error',
  on_hold: 'warning',
};

const MillingOrderList: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<MillingOrder[]>([]);
  const [recipes, setRecipes] = useState<MillingRecipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterRecipe, setFilterRecipe] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOrder, setSelectedOrder] = useState<MillingOrder | null>(null);
  const [createFromRecipeDialog, setCreateFromRecipeDialog] = useState(false);
  const [selectedRecipeId, setSelectedRecipeId] = useState('');

  useEffect(() => {
    fetchOrders();
    fetchRecipes();
  }, [filterStatus, filterRecipe, page, rowsPerPage]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params: any = { page: page + 1, limit: rowsPerPage };
      if (filterStatus) params.status = filterStatus;
      if (filterRecipe) params.recipeId = filterRecipe;
      const response = await millingApi.getOrders(params);
      setOrders(response.data || response);
      setTotal(response.total || response.length);
    } catch (err) {
      console.error(err);
      setError('Error loading orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipes = async () => {
    try {
      const response = await millingApi.getRecipes({ status: 'active' });
      setRecipes(response.data || response);
    } catch (err) {
      console.error(err);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, order: MillingOrder) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrder(order);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedOrder(null);
  };

  const handleAction = async (action: string) => {
    if (!selectedOrder) return;
    handleMenuClose();
    try {
      switch (action) {
        case 'submit':
          await millingApi.submitForApproval(selectedOrder.id!, { updatedBy: 'admin' });
          break;
        case 'approve':
          await millingApi.approveOrder(selectedOrder.id!, { approvedBy: 'admin' });
          break;
        case 'start':
          await millingApi.startOrder(selectedOrder.id!, { updatedBy: 'admin' });
          break;
        case 'complete':
          await millingApi.completeOrder(selectedOrder.id!, { completedBy: 'admin' });
          break;
        case 'cancel':
          await millingApi.cancelOrder(selectedOrder.id!, { cancelledBy: 'admin', reason: 'Cancelled by user' });
          break;
        case 'hold':
          await millingApi.holdOrder(selectedOrder.id!, { updatedBy: 'admin', reason: 'Put on hold' });
          break;
        case 'resume':
          await millingApi.resumeOrder(selectedOrder.id!, { updatedBy: 'admin' });
          break;
      }
      fetchOrders();
    } catch (err) {
      alert(`Error performing action: ${action}`);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try {
      await millingApi.deleteOrder(id);
      fetchOrders();
    } catch (err) {
      alert('Error deleting order');
    }
  };

  const handleCreateFromRecipe = async () => {
    if (!selectedRecipeId) return;
    navigate(`/milling/orders/new?recipeId=${selectedRecipeId}`);
    setCreateFromRecipeDialog(false);
  };

  const getAvailableActions = (status: OrderStatus) => {
    switch (status) {
      case 'draft': return ['submit', 'delete'];
      case 'pending_approval': return ['approve', 'cancel'];
      case 'approved': return ['start', 'cancel'];
      case 'in_progress': return ['complete', 'hold', 'cancel'];
      case 'on_hold': return ['resume', 'cancel'];
      default: return [];
    }
  };

  if (loading && orders.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Milling Orders</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => setCreateFromRecipeDialog(true)}>
            Create from Recipe
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/milling/orders/new')}>
            New Order
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="pending_approval">Pending Approval</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="on_hold">On Hold</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Recipe</InputLabel>
              <Select value={filterRecipe} label="Recipe" onChange={(e) => setFilterRecipe(e.target.value)}>
                <MenuItem value="">All Recipes</MenuItem>
                {recipes.map((r) => <MenuItem key={r.id} value={r.id}>{r.recipeCode}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Recipe</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Planned Qty</TableCell>
                <TableCell align="right">Actual Qty</TableCell>
                <TableCell align="right">Yield Var</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell><Typography fontWeight="bold">{order.orderNumber}</Typography></TableCell>
                  <TableCell>{new Date(order.orderDate).toLocaleDateString()}</TableCell>
                  <TableCell>{order.recipeCode}</TableCell>
                  <TableCell>
                    <Chip label={order.status.replace('_', ' ')} color={statusColors[order.status]} size="small" />
                  </TableCell>
                  <TableCell align="right">{order.plannedOutputQty?.toFixed(2)}</TableCell>
                  <TableCell align="right">{order.actualOutputQty?.toFixed(2) || '-'}</TableCell>
                  <TableCell align="right">
                    {order.yieldVariance !== undefined ? (
                      <Typography color={order.yieldVariance >= 0 ? 'success.main' : 'error.main'}>
                        {order.yieldVariance >= 0 ? '+' : ''}{order.yieldVariance?.toFixed(2)}%
                      </Typography>
                    ) : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View"><IconButton size="small" onClick={() => navigate(`/milling/orders/${order.id}`)}><ViewIcon /></IconButton></Tooltip>
                    {order.status === 'draft' && (
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => navigate(`/milling/orders/${order.id}/edit`)}><EditIcon /></IconButton></Tooltip>
                    )}
                    <IconButton size="small" onClick={(e) => handleMenuClick(e, order)}><MoreIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {orders.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center">No orders found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Card>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {selectedOrder && getAvailableActions(selectedOrder.status).map((action) => (
          <MenuItem key={action} onClick={() => handleAction(action)}>
            {action === 'submit' && <><SubmitIcon sx={{ mr: 1 }} /> Submit for Approval</>}
            {action === 'approve' && <><ApproveIcon sx={{ mr: 1 }} /> Approve</>}
            {action === 'start' && <><StartIcon sx={{ mr: 1 }} /> Start</>}
            {action === 'complete' && <><CompleteIcon sx={{ mr: 1 }} /> Complete</>}
            {action === 'cancel' && <><CancelIcon sx={{ mr: 1 }} /> Cancel</>}
            {action === 'hold' && <><HoldIcon sx={{ mr: 1 }} /> Put on Hold</>}
            {action === 'resume' && <><StartIcon sx={{ mr: 1 }} /> Resume</>}
            {action === 'delete' && <><DeleteIcon sx={{ mr: 1 }} /> Delete</>}
          </MenuItem>
        ))}
      </Menu>

      <Dialog open={createFromRecipeDialog} onClose={() => setCreateFromRecipeDialog(false)}>
        <DialogTitle>Create Order from Recipe</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Recipe</InputLabel>
            <Select value={selectedRecipeId} label="Select Recipe" onChange={(e) => setSelectedRecipeId(e.target.value)}>
              {recipes.map((r) => <MenuItem key={r.id} value={r.id}>{r.recipeCode} - {r.recipeName}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFromRecipeDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateFromRecipe} disabled={!selectedRecipeId}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MillingOrderList;
